// Admin-only "Why is this medication (not) suggested?" debug tool.
// Opened from the 🐞 button on the proactive medication card (ChatPane). Fetches the
// suggestion-engine diagnostics (GET /suggestions?debug=1 — admin only) and authors a
// copy-pasteable prompt an AI can use to diagnose suggestion accuracy and propose code
// changes. The admin can describe the specific problem; that note is folded into the
// prompt, which is then copied to the clipboard in one tap.

import { useEffect, useMemo, useState } from "react";
import {
  getSuggestionsDebug,
  ApiError,
  type ProactiveSuggestion,
} from "../lib/api.js";
import type {
  ProactiveSuggestionDebug,
  ProactiveDebugCandidate,
} from "@medassist/shared";
import { Skeleton } from "./Skeleton.js";
import { ModalShell } from "./ModalShell.js";
import { ClearableTextarea } from "./ClearableInput.js";

// ── Prompt authoring ─────────────────────────────────────────────────────────

function fmtWindow(w: { hoursBefore: number; hoursAfter: number; beforeMin: number; afterMin: number }): string {
  return `−${w.hoursBefore}h/+${w.hoursAfter}h (−${w.beforeMin} min / +${w.afterMin} min)`;
}

function candidateBlock(c: ProactiveDebugCandidate): string {
  const lines: string[] = [];
  lines.push(`### ${c.name} — ${c.included ? "SHOWN" : "NOT SHOWN"} (${c.source})`);
  const facts: string[] = [`group: ${c.group}`];
  if (c.source === "scheduled") {
    facts.push(`regimen: "${c.regimenName ?? "?"}" (kind ${c.regimenKind}, ${c.regimenActive ? "active" : "inactive"})`);
    if (c.defaultTime) facts.push(`regimen defaultTime: ${c.defaultTime}`);
  }
  if (c.anchorTime) facts.push(`anchor: ${c.anchorTime}`);
  if (c.deltaMinutes !== undefined) facts.push(`delta: ${c.deltaMinutes} min`);
  facts.push(`window: ${fmtWindow(c.window)}`);
  if (c.inWindow !== undefined) facts.push(`inWindow: ${c.inWindow}`);
  if (c.alreadyHandled !== undefined) facts.push(`alreadyHandled: ${c.alreadyHandled}`);
  if (c.inactive) facts.push(`inactive: true`);
  if (c.claimedByScheduled) facts.push(`claimedByScheduled: true`);
  if (c.source === "learned") {
    if (c.occurrencesInWindow !== undefined)
      facts.push(`occurrences(in-window): ${c.occurrencesInWindow}/${c.minOccurrencesRequired ?? "?"} required`);
    if (c.distinctDaysInWindow !== undefined) facts.push(`distinct days: ${c.distinctDaysInWindow}`);
    if (c.regularity !== undefined)
      facts.push(`regularity: ${c.regularity.toFixed(2)}/${c.regularityRequired ?? "?"} required`);
  }
  lines.push(`- ${facts.join(" · ")}`);
  lines.push(`- Reasons:`);
  for (const r of c.reasons) lines.push(`  - ${r}`);
  lines.push(`- Last ${c.recentDoses.length} logged dose(s):`);
  if (c.recentDoses.length === 0) {
    lines.push(`  - (none in the look-back window)`);
  } else {
    for (const d of c.recentDoses) {
      const bits = [d.localTime];
      if (d.dose) bits.push(d.dose);
      if (d.scheduledGroup) bits.push(`group ${d.scheduledGroup}`);
      if (d.missed) bits.push("MISSED");
      lines.push(`  - ${bits.join(" — ")}`);
    }
  }
  return lines.join("\n");
}

function buildPrompt(
  debug: ProactiveSuggestionDebug,
  suggestion: ProactiveSuggestion | null,
  userNote: string
): string {
  const s = debug.settings;
  const shown = debug.candidates.filter((c) => c.included);
  const notShown = debug.candidates.filter((c) => !c.included);

  const perDayPart =
    s.perDayPart && Object.keys(s.perDayPart).length
      ? Object.entries(s.perDayPart)
          .map(([g, w]) => `${g}: −${w?.hoursBefore}h/+${w?.hoursAfter}h`)
          .join(", ")
      : "(none)";

  const dayPartTimes = Object.entries(debug.dayPartTimes)
    .map(([g, t]) => `${g} ${t}`)
    .join(", ");

  const currentCard = suggestion
    ? [
        `Headline: "${suggestion.headline}"`,
        ...suggestion.events.map(
          (e) => `- ${e.confirmation}${e.source ? ` (${e.source})` : ""}`
        ),
        suggestion.warningPrefix?.trim()
          ? `Warnings shown: ${suggestion.warningPrefix.trim().replace(/\s+/g, " ")}`
          : `Warnings shown: (none)`,
      ].join("\n")
    : "Nothing is currently being suggested on the card.";

  return `# Debug: Proactive Medication Suggestion Accuracy

You are an AI assistant working in the **MedicalAssistant** codebase. Help me diagnose why the proactive medication suggestion feature is (or is not) recommending a medication right now, and propose code changes to improve its accuracy.

## How the feature works
The engine lives in \`api/src/lib/suggestions.ts\` (\`buildMedicationSuggestions\`). On chat-open the web app (\`web/src/components/ChatPane.tsx\`) calls \`GET /suggestions\` (handler in \`api/src/api.ts\`), which builds medication proposals from two sources with ZERO AI tokens:
- **Source A — scheduled regimens**: each active \`scheduled\` regimen has an anchor time (its \`defaultTime\`, else the patient's day-part time, else an app default). PRN / as-needed-inventory regimens are never auto-suggested.
- **Source B — learned from history**: medications taken regularly around this time of day (needs ≥ \`${debug.tuning.learnedMinOccurrences}\` in-window occurrences and ≥ \`${debug.tuning.learnedRegularity}\` regularity over the last \`${debug.tuning.learnedWindowDays}\` days).

A medication is shown when its anchor time falls inside the patient's configured "around now" window (circular 24h math) AND no matching dose was already logged or marked missed within the window (the "already handled" check, capped at \`${debug.tuning.handledBeforeCapMinutes}\` min before a future anchor). A scheduled regimen claims a med name before the learned source, so the same med isn't proposed twice. Window settings + per-day-part overrides live in \`shared/src/proactiveSuggestions.ts\`. Allergy / interaction / overdose / food checks (\`api/src/api.ts\`) only add warnings — they do not suppress. The client also snoozes the card after Save (2h) / Dismiss (4h) and re-prompts after 15 min of staleness (\`web/src/components/ChatPane.tsx\`).

## Current snapshot
- **Now**: ${debug.nowLocalTime} (${debug.timezone}), minute-of-day ${debug.nowMinuteOfDay}
- **Settings**: enabled=${s.enabled}, windowMode=${s.windowMode}, hoursBefore=${s.hoursBefore}, hoursAfter=${s.hoursAfter}, includeLearned=${s.includeLearned}
- **Per-day-part overrides**: ${perDayPart}
- **Effective global window**: ${fmtWindow(debug.globalWindow)}
- **Day-part times**: ${dayPartTimes}
- **Tuning constants**: learnedWindowDays=${debug.tuning.learnedWindowDays}, minOccurrences=${debug.tuning.learnedMinOccurrences}, regularity=${debug.tuning.learnedRegularity}, handledBeforeCap=${debug.tuning.handledBeforeCapMinutes}min, doneEndGrace=${debug.tuning.doneEndGraceMinutes}min
- **Inactive (paused) meds**: ${debug.inactiveMedNames.length ? debug.inactiveMedNames.join(", ") : "(none)"}
${debug.notes.length ? `\n**Engine notes:**\n${debug.notes.map((n) => `- ${n}`).join("\n")}` : ""}

## Currently suggested on the card
${currentCard}

## Medications shown (${shown.length})
${shown.length ? shown.map(candidateBlock).join("\n\n") : "(none)"}

## Medications evaluated but NOT shown (${notShown.length})
${notShown.length ? notShown.map(candidateBlock).join("\n\n") : "(none)"}

## My problem / what I expected
${userNote.trim() ? userNote.trim() : "<Describe here which medication you expected to be shown (or not shown) right now, and why. Add the medication name you are calling into question.>"}

## What I need from you
1. Explain precisely why the medication(s) in question are or aren't shown, citing the timing math (anchor, delta, window, occurrences/regularity, already-handled) above.
2. Identify any bug, or overly strict / overly loose logic, in \`api/src/lib/suggestions.ts\` (or the settings/defaults in \`shared/src/proactiveSuggestions.ts\`) that produces the wrong result.
3. Propose specific code changes to improve accuracy, with the tradeoffs and any edge cases to watch (cross-day-part suppression, timezone/midnight wrap, learned vs. scheduled dedup).

## Raw diagnostic JSON
\`\`\`json
${JSON.stringify(debug, null, 2)}
\`\`\`
`;
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function SuggestionDebugModal({
  patientId,
  suggestion,
  onClose,
}: {
  patientId: string;
  suggestion: ProactiveSuggestion | null;
  onClose: () => void;
}) {
  const [debug, setDebug] = useState<ProactiveSuggestionDebug | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userNote, setUserNote] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSuggestionsDebug(patientId)
      .then((r) => {
        if (cancelled) return;
        if (!r.debug) {
          setError("No diagnostics returned. The debug view is admin-only.");
        } else {
          setDebug(r.debug);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load suggestion diagnostics");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const prompt = useMemo(
    () => (debug ? buildPrompt(debug, suggestion, userNote) : ""),
    [debug, suggestion, userNote]
  );

  function copyPrompt() {
    if (!prompt) return;
    void navigator.clipboard.writeText(prompt);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <ModalShell
      title="🐞 Suggestion Debug Prompt"
      subtitle="Why this medication is (or isn't) suggested — copy into an AI to debug accuracy"
      size="2xl"
      onClose={onClose}
      headerActions={
        <button
          onClick={copyPrompt}
          disabled={!prompt}
          className="shrink-0 rounded-lg bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50 transition-colors"
        >
          {copied ? "Copied ✓" : "Copy prompt"}
        </button>
      }
      bodyClassName="min-h-0 flex-1 overflow-y-auto p-4 space-y-4"
      footer={
        <button
          onClick={copyPrompt}
          disabled={!prompt}
          className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50 transition-colors"
        >
          {copied ? "Copied to clipboard ✓" : "Copy prompt to clipboard"}
        </button>
      }
    >
      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {!loading && error && (
        <div className="rounded-md bg-red-50 dark:bg-red-950 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && !error && debug && (
        <>
          {/* Problem description — folded into the prompt's "My problem" section. */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Describe the problem (optional — added to the prompt)
            </label>
            <ClearableTextarea
              value={userNote}
              onChange={(e) => setUserNote(e.target.value)}
              rows={3}
              placeholder="Which med did you expect to be suggested (or not) right now, and why? Name the medication you're questioning."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {/* Generated prompt preview */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                Generated prompt
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {debug.candidates.length} candidate(s) evaluated
              </span>
            </div>
            <textarea
              readOnly
              value={prompt}
              rows={20}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full resize-y rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-3 py-2 font-mono text-xs leading-relaxed text-gray-800 dark:text-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </>
      )}
    </ModalShell>
  );
}
